import { convertArrayToObject } from "Services/Entity/helper";
import { EntityId } from "Store/Settings/Entities/PropTypes";
import { getActionType } from "Store/Shared/Actions/Helpers";
import ACTIONS, { CANDIDATE, CREATE_CANDIDATE, FETCH_ALL_CANDIDATES, FETCH_CANDIDATE, FETCH_CANDIDATE_DATASET, SAVE_CANDIDATE, SAVE_CANDIDATE_STATUS } from "./ActionTypes";
import { CandidateId, initialCandidateState } from "./PropTypes";

export default function candidateReducer(state = initialCandidateState, action: any) {
    const { payload } = action;
    switch (action.type) {
        case getActionType(ACTIONS.CREATE_CANDIDATE_VALUES).REQUEST:
        case getActionType(ACTIONS.CREATE_CANDIDATE).REQUEST: {
            return {
                ...state,
                _isCreating: true
            }
        }

        case getActionType(ACTIONS.BACKGROUND_CREATE_CANDIDATE_VALUES).SUCCESS: {
            const { data } = payload;
            const entityId = data[0]?.entity_id;

            const formattedData = data.map(i => ({
                entity_id: i?.entity_id,
                field_id: i?.field_id,
                value_id: i?.value_id
            }));
            const result = convertArrayToObject(formattedData, 'field_id');

            return {
                ...state,
                valuesByEntityIdByFieldId: {
                    ...state.valuesByEntityIdByFieldId,
                    [entityId]: {
                        ...state.valuesByEntityIdByFieldId?.[entityId],
                        ...result
                    }
                }
            }
        }

        case getActionType(ACTIONS.CREATE_CANDIDATE).SUCCESS: {
            const { data } = payload;

            return {
                ...state,
                ids: [
                    ...state.ids,
                    data.id
                ],
                entities: {
                    ...state.entities,
                    [data.id]: {
                        ...state.entities[data.id],
                        ...data,
                        display_name: data.display_name || state.entities[data.id]?.display_name
                    }
                }
            }
        }

        case getActionType(ACTIONS.FETCH_ALL_CANDIDATES).REQUEST:
        case getActionType(ACTIONS.FETCH_CANDIDATES_VALUES).REQUEST:
        case getActionType(ACTIONS.FETCH_CANDIDATE_VALUES).REQUEST: {
            return {
                ...state,
                _isFetching: true
            }
        }

        case getActionType(ACTIONS.UPDATE_CANDIDATE).REQUEST:
        case getActionType(ACTIONS.SAVE_CANDIDATE_VALUES).REQUEST:
        case SAVE_CANDIDATE.REQUEST: {
            return {
                ...state,
                _isPatching: true
            }
        }

        case getActionType(ACTIONS.FETCH_CANDIDATE_OVERVIEW_PAGE).REQUEST: {
            const {
                page_number,
                isFiltering
            } = payload;

            return {
                ...state,
                _isFetching: state.pagination.page_number + 1 !== page_number,
                _isFiltering: isFiltering || (state.pagination.page_number + 1 !== page_number && state._isFetchingPage.length > 0),
                _isFetchingPage: [
                    ...(page_number !== 1 ? state._isFetchingPage : []),
                    page_number
                ],
                pagination: {
                    ...(page_number !== 1
                        ? state.pagination
                        : { ...initialCandidateState.pagination }
                    ),
                    page_number: page_number === 1 ? 1 : page_number
                }
            }
        }

        case getActionType(ACTIONS.FETCH_CANDIDATE_CONCEPT).REQUEST: {
            return {
                ...state,
                _isFetching: true,
                _forceFetch: false
            }
        }

        case getActionType(ACTIONS.FETCH_ALL_CANDIDATES).SUCCESS: {
            const { data } = payload;
            const ids: CandidateId[] = data.map(({ id }) => (id));
            const entities: any = convertArrayToObject(data, 'id');

            return {
                ...state,
                _isFetching: false,
                _forceFetch: true,
                ids,
                entities
            }
        }

        case getActionType(ACTIONS.FETCH_CANDIDATE).SUCCESS: {
            const { data } = payload;
            if (data?.length == 0) return state;

            const entityId = data?.id;
            let ids: EntityId[] = [...state.ids];
            if (!state.ids.includes(entityId)) {
                ids = [entityId, ...state.ids];
            }

            const entities = { ...state.entities, [entityId]: { ...data } };

            return {
                ...state,
                ids,
                entities
            };
        }

        case getActionType(ACTIONS.SAVE_CANDIDATE_VALUES).SUCCESS:
        case getActionType(ACTIONS.FETCH_CANDIDATE_VALUES).SUCCESS: {
            const { data } = payload;

            if (data?.length == 0) return state;

            const entityId = data[0]?.entity_id;

            let ids: EntityId[] = [...state.ids];
            if (!state.ids.includes(entityId)) {
                ids = [entityId, ...state.ids];
            }

            return {
                ...state,
                _isFetching: false,
                _isPatching: false,
                _isCreating: false,
                ids,
                valuesByEntityIdByFieldId: {
                    ...state.valuesByEntityIdByFieldId,
                    [entityId]: {
                        ...(state.valuesByEntityIdByFieldId?.[entityId] || {}),
                        ...convertArrayToObject(data, 'field_id')
                    }
                }
            }
        }

        case getActionType(ACTIONS.UPDATE_CANDIDATE).SUCCESS: {
            const { data } = payload;

            return {
                ...state,
                _isPatching: false,
                _forceFetch: true,
                entities: {
                    ...state.entities,
                    [data.id]: {
                        ...state.entities[data.id],
                        ...data,
                        display_name: data.display_name || state.entities[data.id].display_name
                    }
                }
            }
        }

        case getActionType(ACTIONS.CREATE_CANDIDATE_VALUES).SUCCESS: {
            const { data } = payload;
            const entityId = data[0]?.entity_id;

            return {
                ...state,
                _isCreating: false,
                valuesByEntityIdByFieldId: {
                    ...state.valuesByEntityIdByFieldId,
                    [entityId]: {
                        ...(state.valuesByEntityIdByFieldId?.[entityId] ?? {}),
                        ...convertArrayToObject(data, 'field_id')
                    }
                }
            }
        }

        case getActionType(ACTIONS.FETCH_CANDIDATE_CONCEPT).SUCCESS: {
            const { data } = payload;

            return {
                ...state,
                _isFetching: false,
                _forceFetch: true,
                createFlowId: data?.id || null
            }
        }

        case getActionType(ACTIONS.FETCH_CANDIDATE_OVERVIEW_PAGE).SUCCESS: {
            const {
                data: {
                    data,
                    values,
                    pagination,
                    statistics
                }
            } = payload;
            const ids: any[] = data.map(({ id }) => (id));
            const entities: any = convertArrayToObject(data, 'id');

            const {
                page_number,
                total_pages,
                records
            } = pagination;

            let output: any = {};
            let item: any = [];
            for (const i of values) {
                if (!item[i.entity_id]) {
                    item[i.entity_id] = [];
                    output[i.entity_id] = {};
                }

                item[i.entity_id].push(i);
            }

            for (const i of Object.keys(item)) {
                output[i] = {
                    ...state.valuesByEntityIdByFieldId?.[i]
                }

                for (const j of item[i]) {
                    if (!output[i][j.field_id]) {
                        output[i][j.field_id] = j;
                    }
                }
            }

            return {
                ...state,
                _isFetching: false,
                _isFiltering: false,
                _forceFetch: true,
                ids: Array.from(new Set(
                    [
                        ...state.ids,
                        ...ids
                    ]
                )),
                entities: {
                    ...state.entities,
                    ...entities,
                },
                valuesByEntityIdByFieldId: {
                    ...output,
                    ...state.valuesByEntityIdByFieldId
                },
                statistics,
                pagination: {
                    ...state.pagination,
                    page_number: page_number > state.pagination.page_number ? page_number : state.pagination.page_number,
                    total_pages: total_pages > state.pagination.total_pages ? total_pages : state.pagination.total_pages,
                    records: records > state.pagination.records ? records : state.pagination.records,
                    internal_pages: {
                        ...state.pagination?.internal_pages,
                        [page_number]: ids
                    },
                }
            }
        }







        case FETCH_CANDIDATE_DATASET.REQUEST: {
            return {
                ...state,
                datasets: {
                    ...state.datasets,
                    _isFetching: true,
                    _forceFetch: false
                }
            }
        }

        case SAVE_CANDIDATE.SUCCESS: {
            const { data } = payload;

            return {
                ...state,
                _isPatching: false,
                saved: { ...data }
            }
        }

        case CREATE_CANDIDATE.SUCCESS: {
            const { data } = payload;
            return {
                ...state,
                new: data?.id,
                datasets: {
                    ...state.datasets,
                    _forceFetch: true
                }
            }
        }

        case FETCH_ALL_CANDIDATES.SUCCESS: {
            const { data } = payload;

            return {
                ...state,
                saved: { ...data?.data },
                meta: data?.meta
            }
        }

        case FETCH_CANDIDATE.SUCCESS:
        case SAVE_CANDIDATE_STATUS.SUCCESS: {
            const { data } = payload;
            const key = Object.keys(data)[0];

            return {
                ...state,
                saved: {
                    ...state.saved,
                    [key]: data[key]
                },
                new: ''
            }
        }

        case FETCH_CANDIDATE_DATASET.SUCCESS: {
            const { data } = payload;

            return {
                ...state,
                datasets: {
                    ...state.datasets,
                    _isFetching: false,
                    ids: Object.keys(data),
                    entities: data
                }
            }
        }

        case CANDIDATE.EDIT: {
            const { candidateId, fieldId, valueId, value } = payload;

            return {
                ...state,
                unsaved: {
                    [candidateId]: {
                        ...state.unsaved[candidateId],
                        [fieldId]: {
                            id: valueId,
                            value: value
                        },
                    }
                }
            }
        }

        case CANDIDATE.RESET: {
            return {
                ...state,
                unsaved: {}
            }
        }





        case CREATE_CANDIDATE.FAILURE:
            return { ...state };

        default:
            return { ...state };
    }
}